import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, ExternalLink, X } from "lucide-react";
import { fileUrl } from "@/lib/api";

export function PhotoLightbox({ photos = [], activeId, onClose }) {
  const [index, setIndex] = useState(0);
  const touchStart = useRef(null);

  useEffect(() => {
    if (!activeId) return;
    const nextIndex = photos.findIndex((photo) => (photo._galleryId || photo.id) === activeId);
    setIndex(nextIndex >= 0 ? nextIndex : 0);
  }, [activeId, photos]);

  useEffect(() => {
    if (!activeId) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
      if (event.key === "ArrowLeft") {
        setIndex((current) => (current - 1 + photos.length) % photos.length);
      }
      if (event.key === "ArrowRight") {
        setIndex((current) => (current + 1) % photos.length);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [activeId, onClose, photos.length]);

  if (!activeId || photos.length === 0 || typeof document === "undefined") return null;

  const photo = photos[index] || photos[0];
  const showPrevious = () => setIndex((current) => (current - 1 + photos.length) % photos.length);
  const showNext = () => setIndex((current) => (current + 1) % photos.length);
  const finishSwipe = (event) => {
    if (touchStart.current === null) return;
    const distance = event.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(distance) > 45) {
      if (distance > 0) showPrevious();
      else showNext();
    }
    touchStart.current = null;
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex cursor-default flex-col bg-slate-950/95 text-white backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Fotovorschau"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
      data-testid="photo-lightbox"
    >
      <div className="flex h-16 shrink-0 items-center justify-between gap-3 px-4 sm:px-6">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{photo.filename || "Foto"}</div>
          <div className="text-xs text-white/60">{index + 1} / {photos.length}</div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={fileUrl(photo.storage_path)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
            aria-label="Original in neuem Fenster öffnen"
            title="Original öffnen"
            onClick={(event) => event.stopPropagation()}
          >
            <ExternalLink className="h-5 w-5" />
          </a>
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
            onClick={onClose}
            aria-label="Schließen"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div
        className="relative flex min-h-0 flex-1 items-center justify-center px-3 pb-5 sm:px-16"
        onTouchStart={(event) => { touchStart.current = event.touches[0].clientX; }}
        onTouchEnd={finishSwipe}
      >
        <img
          src={fileUrl(photo.storage_path)}
          alt={photo.filename || "Foto"}
          className="pointer-events-none max-h-full max-w-full select-none object-contain"
          draggable="false"
        />

        {photos.length > 1 && (
          <>
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                showPrevious();
              }}
              className="absolute left-2 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/55 hover:bg-black/80 sm:left-5 sm:h-12 sm:w-12"
              aria-label="Vorheriges Foto"
            >
              <ChevronLeft className="h-7 w-7" />
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                showNext();
              }}
              className="absolute right-2 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/55 hover:bg-black/80 sm:right-5 sm:h-12 sm:w-12"
              aria-label="Nächstes Foto"
            >
              <ChevronRight className="h-7 w-7" />
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
